import firebase from "firebase/app";
import { useEffect, useState } from "react";
import { z } from "zod";

const StreamingAnnouncementSchema = z.object({
  createdAt: z.custom<firebase.firestore.Timestamp>(
    (val) => val instanceof firebase.firestore.Timestamp
  ),
  eventId: z.string(),
  msg: z.string(),
  userId: z.string(),
});
type StreamingAnnouncement = z.infer<typeof StreamingAnnouncementSchema>;

const announcementConverter: firebase.firestore.FirestoreDataConverter<StreamingAnnouncement> =
  {
    fromFirestore(
      snapshot: firebase.firestore.QueryDocumentSnapshot,
      options: firebase.firestore.SnapshotOptions
    ): StreamingAnnouncement {
      const data = snapshot.data(options);
      try {
        return StreamingAnnouncementSchema.parse(data);
      } catch (error) {
        console.error(
          "Zod validation failed for streaming announcement:",
          error
        );
        throw error;
      }
    },
    toFirestore(
      modelObject: StreamingAnnouncement
    ): firebase.firestore.DocumentData {
      return modelObject;
    },
  };

export const useAnnouncement = ({
  mainVideoId,
  handleOpenAnnouncement,
}: {
  mainVideoId: string | undefined;
  handleOpenAnnouncement: () => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [streamingAnnouncement, setStreamingAnnouncement] =
    useState<string>("");

  useEffect(() => {
    if (mainVideoId === undefined) {
      return;
    }

    const msgRef = `/streamingAnnouncement/${mainVideoId}/history`;

    const typedCollectionRef = firebase
      .firestore()
      .collection(msgRef)
      .withConverter(announcementConverter);

    const unsubscribe = typedCollectionRef
      .orderBy("createdAt", "desc")
      .limit(1)
      .onSnapshot(
        { includeMetadataChanges: true },
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              const data = change.doc.data();
              const { msg } = data;

              if (msg.length > 0) {
                setStreamingAnnouncement(msg);
                handleOpenAnnouncement();
              }
            }
          });
        },
        console.error
      );

    return unsubscribe;
  }, [mainVideoId, setStreamingAnnouncement, handleOpenAnnouncement]);

  return {
    isEditing,
    streamingAnnouncement,
    setIsEditing,
    setStreamingAnnouncement,
  };
};
